import IconCalendar from '@assets/icons/calendar.svg?react';
import {
 Box,
 Button,
 FormControl,
 IconButton,
 InputAdornment,
 InputLabel,
 MenuItem,
 Popover,
 Select,
 TextField,
} from '@mui/material';
import {
 DateRangeCalendar,
 DateRangePicker,
 SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

interface DateRangeFilterProps {
 fieldKey: string;
 label: string;
 onDateRangeChange?: (dateFrom: Dayjs | null, dateTo: Dayjs | null) => void;
}

const now = dayjs();
const DateRangeFilter = ({
 fieldKey,
 label,
 onDateRangeChange,
}: DateRangeFilterProps) => {
 const th = useTheme();
 const { t } = useTranslation();

 const [isCustomPopoverOpen, setIsCustomPopoverOpen] = useState(false);
 const datePickerAnchorRef = useRef<HTMLDivElement | null>(null);
 const [draftDateRange, setDraftDateRange] = useState<
  [Dayjs | null, Dayjs | null]
 >([null, null]);
 const [draftPeriod, setDraftPeriod] = useState<number | ''>('');
 const [draftYear, setDraftYear] = useState<number | ''>('');
 const [draftMonth, setDraftMonth] = useState<number | ''>('');
 const { setValue, getValues, watch } = useFormContext();
 const dateFromField = `dateRanges[${fieldKey}].from`;
 const dateToField = `dateRanges[${fieldKey}].to`;
 const periodField = `dateRanges[${fieldKey}].period`;
 const yearField = `dateRanges[${fieldKey}].year`;
 const monthField = `dateRanges[${fieldKey}].month`;

 const periods = [
  {
   value: [now.startOf('isoWeek'), now.endOf('isoWeek')],
   label: t('filterSidebar.thisWeek'),
  },
  {
   value: [
    now.subtract(7, 'day').startOf('isoWeek'),
    now.subtract(7, 'day').endOf('isoWeek'),
   ],
   label: t('filterSidebar.lastWeek'),
  },
  {
   value: [now.startOf('month'), now.endOf('month')],
   label: t('filterSidebar.thisMonth'),
  },
  {
   value: [
    now.subtract(1, 'month').startOf('month'),
    now.subtract(1, 'month').endOf('month'),
   ],
   label: t('filterSidebar.lastMonth'),
  },
 ];

 const watchedDateFrom = watch(dateFromField);
 const watchedDateTo = watch(dateToField);
 const committedDateRange: [Dayjs | null, Dayjs | null] = [
  watchedDateFrom && dayjs(watchedDateFrom).isValid()
   ? dayjs(watchedDateFrom)
   : null,
  watchedDateTo && dayjs(watchedDateTo).isValid() ? dayjs(watchedDateTo) : null,
 ];

 const getSerializedDateRange = (
  dateFrom: Dayjs | null,
  dateTo: Dayjs | null,
 ) => {
  if (!dateFrom && !dateTo) {
   return '';
  }

  const fromValue =
   dateFrom && dayjs(dateFrom).isValid()
    ? dayjs(dateFrom).format('YYYY.MM.DD HH:mm:ss')
    : 'x';
  const toValue =
   dateTo && dayjs(dateTo).isValid()
    ? dayjs(dateTo).format('YYYY.MM.DD HH:mm:ss')
    : 'x';

  return `${fromValue}-${toValue}`;
 };

 const ensureDateOperator = () => {
  if (!getValues(`operators[${fieldKey}]`)) {
   setValue(`operators[${fieldKey}]`, 'is');
  }
 };

 const handleDateRangeInputChange = (
  newValue: [Dayjs | null, Dayjs | null],
 ) => {
  const [nextFrom, nextTo] = newValue;
  const isNextFromInvalid = nextFrom !== null && !dayjs(nextFrom).isValid();
  const isNextToInvalid = nextTo !== null && !dayjs(nextTo).isValid();

  if (isNextFromInvalid || isNextToInvalid) {
   return;
  }

  const serializedDateRange = getSerializedDateRange(nextFrom, nextTo);

  setValue(dateFromField, nextFrom ?? '');
  setValue(dateToField, nextTo ?? '');
  setValue(periodField, '');
  setValue(yearField, '');
  setValue(monthField, '');
  setValue(fieldKey, serializedDateRange, {
   shouldDirty: true,
   shouldTouch: true,
  });

  if (!nextFrom && !nextTo) {
   setValue(`operators[${fieldKey}]`, '');
  } else {
   ensureDateOperator();
  }

  onDateRangeChange?.(nextFrom, nextTo);
 };

 const calculateDateRange = (
  type: 'year' | 'month',
  value: number,
  currentStart: Dayjs | null,
  currentEnd: Dayjs | null,
 ) => {
  let newStart: Dayjs;
  let newEnd: Dayjs;
  const fallbackStart = currentStart ?? dayjs();
  const fallbackEnd = currentEnd ?? fallbackStart;

  switch (type) {
   case 'year':
    if (value < 1900 || value > 2100) return null;
    newStart = fallbackStart.year(value).startOf('year');
    newEnd = fallbackEnd.year(value).endOf('year');
    break;
   case 'month':
    if (value < 1 || value > 12) return null;
    newStart = fallbackStart.month(value - 1).startOf('month');
    newEnd = fallbackEnd.month(value - 1).endOf('month');
    break;
   default:
    return null;
  }

  return [newStart, newEnd] as [Dayjs, Dayjs];
 };

 const handleOpenCustomPopover = () => {
  const currentDateFrom = getValues(dateFromField);
  const currentDateTo = getValues(dateToField);
  const currentPeriod = getValues(periodField);
  const currentYear = getValues(yearField);
  const currentMonth = getValues(monthField);

  setDraftDateRange([
   currentDateFrom ? dayjs(currentDateFrom) : null,
   currentDateTo ? dayjs(currentDateTo) : null,
  ]);
  setDraftPeriod(currentPeriod === '' ? '' : Number(currentPeriod));
  setDraftYear(currentYear === '' ? '' : Number(currentYear));
  setDraftMonth(currentMonth === '' ? '' : Number(currentMonth));
  setIsCustomPopoverOpen(true);
 };

 const handleCloseCustomPopover = () => {
  setIsCustomPopoverOpen(false);
 };

 const handleDraftPeriodChange = (value: number | '') => {
  setDraftPeriod(value);
  setDraftYear('');
  setDraftMonth('');

  if (value === '') {
   setDraftDateRange([null, null]);
   return;
  }

  setDraftDateRange([periods[value].value[0], periods[value].value[1]]);
 };

 const handleDraftDatePartChange = (type: 'year' | 'month', value: number) => {
  const nextDateRange = calculateDateRange(
   type,
   value,
   draftDateRange[0],
   draftDateRange[1],
  );

  if (!nextDateRange) return;

  setDraftDateRange(nextDateRange);
  setDraftPeriod('');
 };

 const handleDraftCalendarChange = (newValue: [Dayjs | null, Dayjs | null]) => {
  setDraftDateRange(newValue);
  setDraftPeriod('');
 };

 const handleSubmitCustomPopover = () => {
  const [draftFrom, draftTo] = draftDateRange;
  const serializedDateRange = getSerializedDateRange(draftFrom, draftTo);

  setValue(dateFromField, draftFrom ?? '');
  setValue(dateToField, draftTo ?? '');
  setValue(periodField, draftPeriod);
  setValue(yearField, draftYear);
  setValue(monthField, draftMonth);
  setValue(fieldKey, serializedDateRange, {
   shouldDirty: true,
   shouldTouch: true,
  });

  if (!draftFrom && !draftTo && draftPeriod === '') {
   setValue(`operators[${fieldKey}]`, '');
  } else {
   ensureDateOperator();
  }

  onDateRangeChange?.(draftFrom, draftTo);

  handleCloseCustomPopover();
 };

 return (
  <Box sx={{ flex: 1 }}>
   <Box>
    <DateRangePicker
     value={committedDateRange}
     onChange={handleDateRangeInputChange}
     ref={datePickerAnchorRef}
     open={false}
     slots={{ field: SingleInputDateRangeField }}
     slotProps={{
      textField: {
       fullWidth: true,
       label,
       InputProps: {
        endAdornment: (
         <InputAdornment position="end">
          <IconButton
           sx={{ width: 24, height: 24, mr: '-6px' }}
           onClick={handleOpenCustomPopover}
           edge="end"
          >
           <IconCalendar
            style={{
             color: th.palette.kemek.gray,
            }}
           />
          </IconButton>
         </InputAdornment>
        ),
       },
      },
     }}
    />
   </Box>
   <Popover
    open={isCustomPopoverOpen}
    anchorEl={datePickerAnchorRef.current}
    onClose={handleCloseCustomPopover}
    anchorOrigin={{
     vertical: 'bottom',
     horizontal: 'left',
    }}
    transformOrigin={{
     vertical: 'top',
     horizontal: 'left',
    }}
   >
    <Box
     sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      p: '12px',
      width: 660,
      minWidth: 660,
     }}
    >
     <Box sx={{ display: 'flex', gap: 1 }}>
      <FormControl sx={{ flex: 1 }}>
       <InputLabel id={`custom-date-range-select-label-${fieldKey}`}>
        {t('filterSidebar.period')}
       </InputLabel>
       <Select
        value={draftPeriod}
        id={`custom-date-range-select-${fieldKey}`}
        labelId={`custom-date-range-select-label-${fieldKey}`}
        label={t('filterSidebar.period')}
        onChange={(event) => {
         const nextValue =
          event.target.value === '' ? '' : Number(event.target.value);
         handleDraftPeriodChange(nextValue);
        }}
        inputProps={{ 'aria-label': t('filterSidebar.period') }}
       >
        <MenuItem value="">Tuščias</MenuItem>
        {periods.map((item, index) => (
         <MenuItem key={item.label} value={index}>
          {item.label}
         </MenuItem>
        ))}
       </Select>
      </FormControl>
      <TextField
       value={draftYear}
       label={t('global.year')}
       type="number"
       onChange={(event) => {
        const nextValue = parseInt(event.target.value, 10);

        if (Number.isNaN(nextValue)) {
         setDraftYear('');
         return;
        }

        setDraftYear(nextValue);
        handleDraftDatePartChange('year', nextValue);
       }}
       sx={{ flex: 1, maxWidth: 115 }}
       slotProps={{
        htmlInput: {
         step: 1,
         min: 1900,
         max: 2100,
        },
       }}
      />
      <TextField
       value={draftMonth}
       label={t('global.month')}
       type="number"
       onChange={(event) => {
        const nextValue = parseInt(event.target.value, 10);

        if (Number.isNaN(nextValue)) {
         setDraftMonth('');
         return;
        }

        setDraftMonth(nextValue);
        handleDraftDatePartChange('month', nextValue);
       }}
       sx={{ flex: 1, maxWidth: 115 }}
       slotProps={{
        htmlInput: {
         step: 1,
         min: 1,
         max: 12,
        },
       }}
      />
     </Box>
     <DateRangeCalendar
      value={draftDateRange}
      onChange={handleDraftCalendarChange}
     />
     <Box
      sx={{
       display: 'flex',
       justifyContent: 'flex-end',
       gap: 1,
       pt: '12px',
       borderTop: (theme) => `1px solid ${theme.palette.kemek.lightGray}`,
      }}
     >
      <Button
       variant="outlined"
       color="secondary"
       onClick={handleCloseCustomPopover}
      >
       {t('global.cancel')}
      </Button>
      <Button variant="contained" onClick={handleSubmitCustomPopover}>
       {t('global.apply')}
      </Button>
     </Box>
    </Box>
   </Popover>
  </Box>
 );
};
export default DateRangeFilter;

